import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

/** Mirror of the backend password policy in routers/auth.py.
 *  Keep in sync — both layers must agree. */
const POLICY = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special symbol (e.g. !@#$%)', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(p) },
];

/** Returns true if `pw` passes ALL the CivilX TMS policy rules. */
export const isPasswordValid = (pw) => POLICY.every((r) => r.test(pw || ''));

const PasswordStrengthMeter = ({ password = '', testid = 'password-strength' }) => {
  const results = useMemo(
    () => POLICY.map((r) => ({ ...r, ok: r.test(password) })),
    [password],
  );
  const score = results.filter((r) => r.ok).length;
  const barColor =
    score <= 2 ? 'bg-red-500' :
    score === 3 ? 'bg-amber-500' :
    score === 4 ? 'bg-yellow-500' :
    'bg-emerald-500';
  const barLabel =
    score <= 2 ? 'Weak' :
    score === 3 ? 'Fair' :
    score === 4 ? 'Good' :
    'Strong';

  return (
    <div data-testid={testid} className="mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            data-testid={`${testid}-bar`}
            className={`h-full transition-all duration-200 ${barColor}`}
            style={{ width: `${(score / POLICY.length) * 100}%` }}
          />
        </div>
        <span data-testid={`${testid}-label`} className="text-[11px] font-bold uppercase tracking-widest text-slate-500 min-w-[44px]">
          {password ? barLabel : ''}
        </span>
      </div>
      <ul className="space-y-0.5">
        {results.map((r) => (
          <li
            key={r.id}
            data-testid={`${testid}-rule-${r.id}`}
            className={`text-[11px] flex items-center gap-1.5 ${r.ok ? 'text-emerald-600' : 'text-slate-500'}`}
          >
            {r.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3 text-slate-300" />}
            {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordStrengthMeter;
